import { useEffect, useRef } from 'react';
import type { VoiceState } from '@acde/shared';

interface WaveAnimationProps {
  state: VoiceState;
  size?: number;
}

/**
 * Professional voice orb animation inspired by OpenAI's advanced voice mode.
 * Uses canvas-based rendering for smooth, dynamic audio visualization.
 */
export function WaveAnimation({ state, size = 280 }: WaveAnimationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set up high DPI canvas
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    const centerX = size / 2;
    const centerY = size / 2;
    const baseRadius = size * 0.28;

    const animate = () => {
      timeRef.current += 0.016; // ~60fps
      const time = timeRef.current;

      // Clear canvas
      ctx.clearRect(0, 0, size, size);

      // State-based configurations
      const isActive = state !== 'idle';
      const isSpeaking = state === 'speaking';
      const isListening = state === 'listening';
      const isConnecting = state === 'connecting' || state === 'processing';

      // Dynamic intensity based on state
      let intensity = 0.3;
      let speed = 1;
      let glowIntensity = 0.4;

      if (isSpeaking) {
        intensity = 0.8 + Math.sin(time * 8) * 0.15;
        speed = 2.5;
        glowIntensity = 0.7;
      } else if (isListening) {
        intensity = 0.5 + Math.sin(time * 3) * 0.1;
        speed = 1.5;
        glowIntensity = 0.5;
      } else if (isConnecting) {
        intensity = 0.4 + Math.sin(time * 5) * 0.2;
        speed = 2;
        glowIntensity = 0.4;
      }

      // Outer glow layers
      for (let i = 4; i >= 0; i--) {
        const glowRadius = baseRadius + (i + 1) * 18 * intensity;
        const alpha = glowIntensity * (0.08 - i * 0.015);
        
        const gradient = ctx.createRadialGradient(
          centerX, centerY, baseRadius * 0.5,
          centerX, centerY, glowRadius
        );
        
        if (isSpeaking) {
          gradient.addColorStop(0, `rgba(16, 185, 129, ${alpha * 1.5})`);
          gradient.addColorStop(0.5, `rgba(6, 182, 212, ${alpha})`);
          gradient.addColorStop(1, 'rgba(6, 182, 212, 0)');
        } else if (isConnecting) {
          gradient.addColorStop(0, `rgba(251, 191, 36, ${alpha * 1.2})`);
          gradient.addColorStop(0.5, `rgba(245, 158, 11, ${alpha})`);
          gradient.addColorStop(1, 'rgba(245, 158, 11, 0)');
        } else {
          gradient.addColorStop(0, `rgba(0, 43, 254, ${alpha * 1.2})`);
          gradient.addColorStop(0.5, `rgba(3, 127, 243, ${alpha})`);
          gradient.addColorStop(1, 'rgba(3, 127, 243, 0)');
        }
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(centerX, centerY, glowRadius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Dynamic morphing orb
      if (isActive) {
        ctx.save();
        
        // Create morphing blob path
        ctx.beginPath();
        const points = 64;
        for (let i = 0; i <= points; i++) {
          const angle = (i / points) * Math.PI * 2;
          
          // Multiple wave frequencies for organic movement
          const wave1 = Math.sin(angle * 3 + time * speed * 2) * 4 * intensity;
          const wave2 = Math.sin(angle * 5 + time * speed * 1.5) * 3 * intensity;
          const wave3 = Math.sin(angle * 7 + time * speed * 3) * 2 * intensity;
          
          // Breathing effect
          const breathe = Math.sin(time * speed) * 3 * intensity;
          
          const r = baseRadius + wave1 + wave2 + wave3 + breathe;
          const x = centerX + Math.cos(angle) * r;
          const y = centerY + Math.sin(angle) * r;
          
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.closePath();
        
        // Gradient fill for the orb
        const orbGradient = ctx.createRadialGradient(
          centerX - baseRadius * 0.3, centerY - baseRadius * 0.3, 0,
          centerX, centerY, baseRadius * 1.3
        );
        
        if (isSpeaking) {
          orbGradient.addColorStop(0, 'rgba(52, 211, 153, 0.95)');
          orbGradient.addColorStop(0.4, 'rgba(16, 185, 129, 0.9)');
          orbGradient.addColorStop(0.7, 'rgba(6, 182, 212, 0.85)');
          orbGradient.addColorStop(1, 'rgba(8, 145, 178, 0.8)');
        } else if (isConnecting) {
          orbGradient.addColorStop(0, 'rgba(253, 230, 138, 0.95)');
          orbGradient.addColorStop(0.4, 'rgba(251, 191, 36, 0.9)');
          orbGradient.addColorStop(0.7, 'rgba(245, 158, 11, 0.85)');
          orbGradient.addColorStop(1, 'rgba(217, 119, 6, 0.8)');
        } else {
          orbGradient.addColorStop(0, 'rgba(99, 142, 255, 0.95)');
          orbGradient.addColorStop(0.4, 'rgba(0, 43, 254, 0.9)');
          orbGradient.addColorStop(0.7, 'rgba(3, 127, 243, 0.85)');
          orbGradient.addColorStop(1, 'rgba(0, 43, 254, 0.8)');
        }
        
        ctx.fillStyle = orbGradient;
        ctx.fill();
        
        // Inner highlight for depth
        const highlightGradient = ctx.createRadialGradient(
          centerX - baseRadius * 0.25, centerY - baseRadius * 0.25, 0,
          centerX, centerY, baseRadius * 0.8
        );
        highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.35)');
        highlightGradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.1)');
        highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        
        ctx.fillStyle = highlightGradient;
        ctx.beginPath();
        ctx.arc(centerX, centerY, baseRadius * 0.9, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.restore();
      } else {
        // Idle state - static gradient orb
        const orbGradient = ctx.createRadialGradient(
          centerX - baseRadius * 0.3, centerY - baseRadius * 0.3, 0,
          centerX, centerY, baseRadius * 1.1
        );
        orbGradient.addColorStop(0, 'rgba(99, 142, 255, 0.6)');
        orbGradient.addColorStop(0.5, 'rgba(0, 43, 254, 0.5)');
        orbGradient.addColorStop(1, 'rgba(0, 10, 20, 0.4)');
        
        ctx.fillStyle = orbGradient;
        ctx.beginPath();
        ctx.arc(centerX, centerY, baseRadius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Sound wave rings when speaking
      if (isSpeaking) {
        const numRings = 3;
        for (let i = 0; i < numRings; i++) {
          const ringProgress = ((time * 0.5 + i / numRings) % 1);
          const ringRadius = baseRadius + ringProgress * size * 0.35;
          const ringAlpha = (1 - ringProgress) * 0.25;
          
          ctx.strokeStyle = `rgba(16, 185, 129, ${ringAlpha})`;
          ctx.lineWidth = 2 * (1 - ringProgress);
          ctx.beginPath();
          ctx.arc(centerX, centerY, ringRadius, 0, Math.PI * 2);
          ctx.stroke();
        }
      }

      // Particle effects when listening
      if (isListening) {
        const numParticles = 8;
        for (let i = 0; i < numParticles; i++) {
          const particleAngle = (i / numParticles) * Math.PI * 2 + time * 0.5;
          const particleDistance = baseRadius + 15 + Math.sin(time * 2 + i) * 10;
          const px = centerX + Math.cos(particleAngle) * particleDistance;
          const py = centerY + Math.sin(particleAngle) * particleDistance;
          const particleSize = 3 + Math.sin(time * 3 + i) * 1.5;
          
          const particleGradient = ctx.createRadialGradient(px, py, 0, px, py, particleSize * 2);
          particleGradient.addColorStop(0, 'rgba(99, 142, 255, 0.8)');
          particleGradient.addColorStop(1, 'rgba(99, 142, 255, 0)');
          
          ctx.fillStyle = particleGradient;
          ctx.beginPath();
          ctx.arc(px, py, particleSize * 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [state, size]);

  const getStateLabel = () => {
    switch (state) {
      case 'connecting':
        return 'Connecting...';
      case 'listening':
        return 'Listening';
      case 'speaking':
        return 'Speaking';
      case 'processing':
        return 'Processing...';
      default:
        return 'Ready';
    }
  };

  return (
    <div className="relative flex flex-col items-center justify-center">
      <canvas
        ref={canvasRef}
        style={{ width: size, height: size }}
        className="drop-shadow-2xl"
      />
      <div className="absolute -bottom-2 text-center">
        <span className="text-white/70 text-sm font-medium tracking-wide">
          {getStateLabel()}
        </span>
      </div>
    </div>
  );
}
